import { AlertTriangle, Download, History, RotateCcw } from 'lucide-react'
import type { RecoveryCandidate } from '../recovery'
import { formatBytes } from '../storage'
import { formatDateTime } from '../utils'

interface RecoveryDialogProps {
  candidate: RecoveryCandidate
  busy: boolean
  onRecover: () => void
  onUseSaved: () => void
  onExport: () => void
}

export function RecoveryDialog({ candidate, busy, onRecover, onUseSaved, onExport }: RecoveryDialogProps) {
  const { pending, savedProject, savedProjectDamaged } = candidate
  const canUseSaved = Boolean(savedProject) && !savedProjectDamaged

  return (
    <div className="modal-backdrop recovery-backdrop" role="presentation">
      <section className="modal recovery-dialog" role="alertdialog" aria-modal="true" aria-labelledby="recovery-title">
        <div className="recovery-dialog-icon"><History size={25} /></div>
        <span className="modal-kicker">Work recovery</span>
        <h2 id="recovery-title">Deckelier found unsaved work</h2>
        <p className="recovery-dialog-copy">
          A save for <strong>{pending.projectName}</strong> did not finish cleanly. The exact revision Deckelier was writing is still available locally.
        </p>
        <div className="recovery-comparison">
          <div className="recommended">
            <span>Recovery copy</span>
            <strong>{formatDateTime(pending.projectUpdatedAt)}</strong>
            <small>{formatBytes(pending.byteSize)} · Recommended</small>
          </div>
          <div className={!canUseSaved ? 'damaged' : ''}>
            <span>Last normal save</span>
            <strong>{savedProject ? formatDateTime(savedProject.updatedAt) : 'Not available'}</strong>
            <small>{savedProjectDamaged ? 'The saved project is damaged' : savedProject ? 'Older, known save' : 'No earlier save exists'}</small>
          </div>
        </div>
        {savedProjectDamaged && <p className="recovery-warning"><AlertTriangle size={16} /> The normal project record cannot be opened. Recovering this copy is the safest option.</p>}
        <div className="modal-actions recovery-actions">
          <div>
            <button type="button" className={canUseSaved ? 'button secondary' : 'button danger'} disabled={busy} onClick={onUseSaved}>
              {canUseSaved ? 'Use last saved' : 'Discard recovery'}
            </button>
            <button type="button" className="button ghost" disabled={busy} onClick={onExport}><Download size={15} /> Download copy</button>
          </div>
          <button type="button" className="button primary" autoFocus disabled={busy} onClick={onRecover}>
            <RotateCcw size={16} /> {busy ? 'Recovering…' : 'Recover unsaved work'}
          </button>
        </div>
      </section>
    </div>
  )
}
